import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { GET_PAGINATED_PRODUCT_CATEGORIES, SAVE_PRODUCT_CATEGORY, DELETE_PRODUCT_CATEGORY } from '../products.graphql';
import { PaginatedProductCategoriesResponse, SaveProductCategoryResponse } from '../types/product-category';
import { ProductCategoryActions } from './product-category.actions';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { SwalService } from '@shared/services/swal.service';

@Injectable()
export class ProductCategoryEffects {
  private actions$ = inject(Actions);
  private graphqlService = inject(GraphQlService);
  private swalService = inject(SwalService);

  loadPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCategoryActions.loadPaginated),
      switchMap((payload) =>
        this.graphqlService
          .query<PaginatedProductCategoriesResponse>({
            query: GET_PAGINATED_PRODUCT_CATEGORIES,
            variables: payload,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const data = response.data.getPaginatedProductCategories;
              return ProductCategoryActions.loadPaginatedSuccess({
                items: data.content,
                totalElements: data.totalElements,
                number: data.number,
                size: data.size,
              });
            }),
            catchError((error) => of(ProductCategoryActions.loadPaginatedFailure({ error })))
          )
      )
    )
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCategoryActions.save),
      switchMap((payload) =>
        this.graphqlService
          .mutate<SaveProductCategoryResponse>({
            mutation: SAVE_PRODUCT_CATEGORY,
            variables: payload,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const result = response.data.saveProductCategory;
              if (result.status) {
                return ProductCategoryActions.saveSuccess({ item: result.data });
              } else {
                return ProductCategoryActions.saveFailure({ error: result.message });
              }
            }),
            catchError((error) => of(ProductCategoryActions.saveFailure({ error: error.message || 'An error occurred' })))
          )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCategoryActions.delete),
      switchMap(({ uuid }) =>
        this.graphqlService
          .mutate<any>({
            mutation: DELETE_PRODUCT_CATEGORY,
            variables: { uuid },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const result = response.data.deleteProductCategory;
              if (result.status) {
                return ProductCategoryActions.deleteSuccess({ uuid, message: result.message });
              } else {
                return ProductCategoryActions.deleteFailure({ error: result.message });
              }
            }),
            catchError((error) => of(ProductCategoryActions.deleteFailure({ error: error.message || 'An error occurred' })))
          )
      )
    )
  );

  notifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProductCategoryActions.saveSuccess,
          ProductCategoryActions.saveFailure,
          ProductCategoryActions.deleteSuccess,
          ProductCategoryActions.deleteFailure
        ),
        tap((action) => {
          if (action.type === ProductCategoryActions.saveSuccess.type) {
            this.swalService.successToast('Product category saved successfully');
          } else if (action.type === ProductCategoryActions.deleteSuccess.type) {
            this.swalService.successToast(action.message || 'Product category deleted successfully');
          } else if (action.type === ProductCategoryActions.deleteFailure.type) {
            const anyAction: any = action;
            this.swalService.error(anyAction.error?.toString?.() ?? 'Operation failed');
          } else if (action.type === ProductCategoryActions.saveFailure.type) {
            const anyAction: any = action;
            this.swalService.errorToast(anyAction.error?.toString?.() ?? 'Operation failed');
          }
        })
      ),
    { dispatch: false }
  );
}
